<template>
  <div>
    <b-card>
      <b-container fluid>
        <b-form @submit.prevent="applySearch">
          <b-row>
            <b-col
              md="4"
            >
              <label for="states">Search by state</label>
              <v-select
                id="states"
                v-model="currState"
                placeholder="No state selected"
                :options="stateOptions"
                label="label"
                :reduce="(option) => option.key"
                @input="applySearch"
              />
            </b-col>

            <b-col
              md="4"
            >
              <label for="trip">Search by trip number</label>
              <b-form-input
                id="trip"
                v-model="queryParams.tripId"
                type="number"
                placeholder="Trip number"
              />
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col
              md="4"
            >
              <label for="date1">From Date</label>
              <b-form-datepicker
                id="date1"
                v-model="queryParams.from"
              />
            </b-col>
            <b-col
              md="4"
            >
              <label for="date2">To Date</label>
              <b-form-datepicker
                id="date2"
                v-model="queryParams.to"
              />
            </b-col>
            <b-col md="4">
              <template>
                <label
                  for="driver"
                >Search by Driver Phone number</label>
                <b-form-input
                  id="driver"
                  v-model="queryParams.driverPhone"
                  type="number"
                  placeholder="Driver Phone number"
                />
              </template>

              <div class="mt-2 d-flex justify-content-between align-items-end">
                <b-button
                  variant="primary"
                  type="submit"
                >
                  Search
                </b-button>
                <b-button
                  variant="outline-primary"
                  @click="clearFilter"
                >
                  Clear
                </b-button>
                <b-button
                  class="btn-icon rounded-circle ml-1"
                  variant="flat-primary"
                  type="submit"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                  />
                </b-button>
                <h6>
                  Total: {{ totalTrips }}
                </h6>
              </div>
            </b-col>

          </b-row>
        </b-form>
      </b-container>
      <b-overlay
        variant="white"
        spinner-variant="primary"
        blur="0"
        opacity=".75"
        rounded="sm"
        :show="loading"
      >
        <b-table
          :fields="tableColumns"
          :items="tripsList"
          hover
          striped
          show-empty
          empty-text="No matching trips found"
          class="text-center"
          @row-clicked="redirectToTripDetails"
        >
          <template #cell(state)="data">
            <trip-status-badge :status="data.item.state" />
          </template>
        </b-table>
        <base-pagination
          v-model="queryParams.page"
          :total="totalTrips"
          @input="fetchTripsList"
          @new-per-page="queryParams.perPage = $event"
        />
      </b-overlay></b-card>
  </div>
</template>

<script>
import {
  BTable, BCard, BOverlay, BFormDatepicker, BContainer, BRow, BCol, BButton, BFormInput, BForm,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  reactive, ref, toRef, watch,
} from '@vue/composition-api'
import tripStatusBadge from '@/views/trips/components/TripStatusBadge.vue'
import BasePagination from '@/UI/BasePagination.vue'
import { formatDate } from '@/@core/utils/filter'
import { tripStatusFormatter } from '@/views/trips/hooks/tripStatusUtils'
import axiosIns from '@/libs/axios'

export default {
  components: {
    BTable,
    BCard,
    BOverlay,
    tripStatusBadge,
    BasePagination,
    vSelect,
    BFormDatepicker,
    BContainer,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormInput,
  },
  setup(props, ctx) {
    const businessId = ctx.root.$route.params.id

    const redirectToTripDetails = $event => {
      const r = ctx.root.$router.resolve(`/trips/${$event.id}`)
      window.open(r.href, '_blank')
    }

    const loading = ref(false)
    const tableColumns = [
      { key: 'number', label: 'trip number' },
      {
        key: 'created_at',
        label: 'created at',
        formatter: value => formatDate(value),
      },
      {
        label: 'Rider',
        key: 'user_details',
        formatter: val => `${val?.first_name} ${val?.last_name}`,
      },
      {
        label: 'Driver',
        key: 'driver',
        formatter: val => val && `${val.first_name} ${val.last_name}`,
      },
      {
        label: 'Zone',
        key: 'zone',
        formatter: val => val?.split('::')[1],
      },
      {
        label: 'Type',
        key: 'class_name',
      },
      {
        label: 'status',
        key: 'state',
        formatter: val => tripStatusFormatter(val),
      },
    ]

    const stateOptions = [
      {
        label: 'All',
        key: 'ALL',
      },
      {
        label: 'Completed',
        key: 'COMPLETED',
      },
      {
        label: 'Created',
        key: 'CREATED',
      },
      {
        label: 'Canceled',
        key: 'CANCELED',
      },
      {
        label: 'Accepted',
        key: 'ACCEPTED',
      },
      {
        label: 'Arrived',
        key: 'ARRIVED',
      },
      {
        label: 'In progress',
        key: 'IN_PROGRESS',
      },
      {
        label: 'Looking for driver',
        key: 'LOOKING_FOR_DRIVER',
      },
      {
        label: 'No driver found',
        key: 'NO_DRIVER_FOUND',
      },
    ]
    const tripsList = ref([])
    const totalTrips = ref(0)

    const queryParams = reactive({
      size: 10,
      page: 1,
      driverPhone: undefined,
      role: 'driver',
      fleet_business_id: businessId,
      state: undefined,
      from: undefined,
      to: undefined,
      tripId: undefined,
    })
    const currState = ref('ALL')
    const fetchTripsList = async () => {
      try {
        loading.value = true
        if (currState.value === 'ALL') {
          queryParams.state = undefined
        } else {
          queryParams.state = currState.value
        }
        if (!queryParams.tripId) {
          queryParams.tripId = undefined
        }
        const res = await axiosIns.get(`/businesses/${businessId}/trips`, {
          params: queryParams,
        })
        const { trips, total } = res.data.data
        tripsList.value = trips
        totalTrips.value = total
      } catch (error) {
        console.log(error)
      } finally {
        loading.value = false
      }
    }
    const applySearch = () => {
      queryParams.page = 1
      fetchTripsList()
    }
    const clearFilter = () => {
      currState.value = 'ALL'
      queryParams.state = undefined
      queryParams.from = undefined
      queryParams.to = undefined
      queryParams.driverPhone = undefined
      queryParams.tripId = undefined
      fetchTripsList()
    }
    fetchTripsList()
    watch([toRef(queryParams, 'size')], () => {
      fetchTripsList()
    })
    // const canSearch = computed(() => filterUserNumber.value || tripId.value)
    return {
      // canSearch,
      applySearch,
      tableColumns,
      fetchTripsList,
      tripsList,
      loading,
      totalTrips,
      stateOptions,
      queryParams,
      redirectToTripDetails,
      clearFilter,
      currState,
    }
  },

}
</script>
